import { useCallback, useState } from 'react';
import {
  Button,
  Checkbox,
  Content,
  H2,
  H4,
  Inline,
  InlineItem,
  Input,
  Secondary,
  Stack,
  Text,
} from '@kinesis/bungle';
import { every, identity, isEmpty, noop } from 'lodash/fp';
import { AnchorText } from '@/components/anchor-text';
import { Center } from '@/components/center';
import { KinesisLogo } from '@/components/kinesis-logo';
import { ShareAccount } from '@/api/share';

// The claim screen for a recipient with no account that can log in: the
// account details are captured here and the share is claimed with them in
// one step. The email is not editable and is not sent — the share is
// claimed for the address it was issued to.
const ShareClaimForm = (props: ShareClaimFormProps) => {
  const {
    onClaim = noop,
    isClaiming = false,
    email,
    label,
    sharerName,
  } = props;

  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(false);
  const validateFirstName = useCallback(() => {
    const valid = !isEmpty(firstName);
    setFirstNameError(!valid);
    return valid;
  }, [firstName]);

  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(false);
  const validateLastName = useCallback(() => {
    const valid = !isEmpty(lastName);
    setLastNameError(!valid);
    return valid;
  }, [lastName]);

  const [jobTitle, setJobTitle] = useState('');

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const validatePassword = useCallback(() => {
    const valid = password.length >= 8;
    setPasswordError(!valid);
    return valid;
  }, [password]);

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptTermsError, setAcceptTermsError] = useState(false);
  const toggleAcceptTerms = useCallback(
    () => setAcceptTerms((current) => !current),
    [],
  );
  const validateAcceptTerms = useCallback(() => {
    setAcceptTermsError(!acceptTerms);
    return acceptTerms;
  }, [acceptTerms]);

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const valid = every(identity, [
        validateAcceptTerms(),
        validateFirstName(),
        validateLastName(),
        validatePassword(),
      ]);
      if (valid) {
        onClaim({
          firstName,
          lastName,
          jobTitle,
          password,
          acceptTerms,
        });
      }
    },
    [
      validateAcceptTerms,
      validateFirstName,
      validateLastName,
      validatePassword,
      firstName,
      lastName,
      jobTitle,
      password,
      acceptTerms,
      onClaim,
    ],
  );

  return (
    <Content background='white' borderRadiusSize='large' width={304}>
      <Stack space='none'>
        <Content padding='large' sizing='fill-container' paddingMode='equal'>
          <Stack space='medium'>
            <Center>
              <KinesisLogo />
            </Center>
            <Center>
              <H2>Shared with you</H2>
            </Center>
            <Text>
              {sharerName ? `${sharerName} shared ` : 'Someone shared '}
              <strong>{label}</strong> with you. Set up your account to open it.
            </Text>
            <Secondary>{email}</Secondary>
          </Stack>
        </Content>
        <Content
          background='gray'
          borderTop
          borderBottom
          sizing='fill-container'
          padding='medium'
          paddingMode='equal'
        >
          <form onSubmit={onSubmit} noValidate>
            <Stack space='large'>
              <Stack space='medium'>
                <H4>Your account</H4>
                <Inline space='small'>
                  <InlineItem sizing='fill-container'>
                    <Input
                      autoFocus
                      label='First name'
                      magnitude='large'
                      onBlur={validateFirstName}
                      onChange={setFirstName}
                      placeholder='Enter first name'
                      tone={firstNameError ? 'critical' : 'neutral'}
                      type='text'
                      value={firstName}
                    />
                  </InlineItem>
                  <InlineItem sizing='fill-container'>
                    <Input
                      label='Last name'
                      magnitude='large'
                      onBlur={validateLastName}
                      onChange={setLastName}
                      placeholder='Enter last name'
                      tone={lastNameError ? 'critical' : 'neutral'}
                      type='text'
                      value={lastName}
                    />
                  </InlineItem>
                </Inline>
                <Input
                  label='Job title'
                  magnitude='large'
                  onChange={setJobTitle}
                  placeholder='Enter job title'
                  type='text'
                  value={jobTitle}
                />
                <Input
                  label='Password'
                  magnitude='large'
                  message='At least 8 characters.'
                  onBlur={validatePassword}
                  onChange={setPassword}
                  placeholder='Enter password'
                  tone={passwordError ? 'critical' : 'neutral'}
                  type='password'
                  value={password}
                />
                <Checkbox
                  checked={acceptTerms}
                  onBlur={validateAcceptTerms}
                  onChange={toggleAcceptTerms}
                  tone={acceptTermsError ? 'critical' : undefined}
                >
                  I agree to the Kinesis{' '}
                  <AnchorText
                    href='https://kinesis.org/privacy/'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    privacy policy
                  </AnchorText>{' '}
                  and{' '}
                  <AnchorText
                    href='https://kinesis.org/terms/'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    terms of use
                  </AnchorText>
                </Checkbox>
              </Stack>
              <Button type='submit' display='block' loading={isClaiming}>
                Create account and open
              </Button>
            </Stack>
          </form>
        </Content>
      </Stack>
    </Content>
  );
};

export type ShareClaimFormProps = {
  onClaim?: (v: ShareAccount) => void;
  isClaiming?: boolean;
  email: string;
  label: string;
  sharerName?: string | null;
};

export { ShareClaimForm };
